import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, X, Plus, Trash2, CheckCircle2, XCircle, ChevronDown } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { OUTCOME_LABEL, MEASURE_LABEL, fmtDateTime, fmtEuro, addressLine } from "@/lib/format";
import { Empty } from "@/components/Common";
import { TypeBadge } from "@/components/StatusBadge";
import { ReportView, AttachmentGrid } from "@/components/ReportView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Evidence = ({ report }) => {
  const order = report.order;
  const req = report.requirements || {};
  const photos = (order.attachments || []).filter((a) => a.mime?.startsWith("image/") && (!a.visit_id || a.visit_id === report.visit_id)).length;
  const checks = [
    { label: "Esito e causale codificati", ok: !!report.outcome && !!report.reason_code },
    { label: `Foto: ${photos} / ${req.min_photos || 0} richieste`, ok: photos >= (req.min_photos || 0) },
    ...(req.required_measures || []).map((m) => ({ label: `Misura: ${MEASURE_LABEL[m] || m}`, ok: !!report.measures?.[m] })),
    { label: "Orari arrivo e partenza", ok: !!report.arrived_at && !!report.departed_at },
  ];
  return (
    <ul className="grid grid-cols-2 gap-2" data-testid="validation-evidence">
      {checks.map((c) => (
        <li key={c.label} className={`flex items-center gap-2 border px-3 py-2 text-sm ${c.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
          {c.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}{c.label}
        </li>
      ))}
    </ul>
  );
};

const Lines = ({ items, lines, setLines, disabled }) => {
  const price = (code) => items.find((i) => i.code === code)?.price || 0;
  const total = lines.reduce((s, l) => s + price(l.code) * (Number(l.qty) || 0), 0);
  const upd = (idx, k, v) => setLines(lines.map((x, i) => (i === idx ? { ...x, [k]: v } : x)));
  return (
    <div data-testid="validation-lines">
      {lines.map((l, idx) => (
        <div key={idx} className="mb-2 grid grid-cols-[1fr_90px_100px_36px] items-center gap-2">
          <Select value={l.code} onValueChange={(v) => upd(idx, "code", v)} disabled={disabled}>
            <SelectTrigger data-testid={`validation-line-code-${idx}`}><SelectValue /></SelectTrigger>
            <SelectContent>{items.map((i) => <SelectItem key={i.code} value={i.code}>{i.code} — {i.description} ({fmtEuro(i.price)})</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" min="0" value={l.qty} disabled={disabled} onChange={(e) => upd(idx, "qty", e.target.value)} data-testid={`validation-line-qty-${idx}`} />
          <span className="text-right font-mono text-sm">{fmtEuro(price(l.code) * (Number(l.qty) || 0))}</span>
          <Button size="icon" variant="ghost" disabled={disabled} onClick={() => setLines(lines.filter((_, i) => i !== idx))}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <div className="mt-3 flex items-center justify-between">
        <Button variant="outline" size="sm" disabled={disabled || !items.length} onClick={() => setLines([...lines, { code: items[0].code, qty: 1 }])} data-testid="validation-add-line"><Plus className="mr-1 h-4 w-4" />Aggiungi voce</Button>
        <div className="font-mono text-lg font-semibold" data-testid="validation-total">Totale {fmtEuro(total)}</div>
      </div>
    </div>
  );
};

const Detail = ({ report, onDone }) => {
  const { can } = useAuth();
  const { nameOf } = useData();
  const order = report.order;
  const items = useMemo(() => report.price_list?.items || [], [report]);
  const [lines, setLines] = useState([]);
  const [note, setNote] = useState("");
  const allowed = can("production:validate");

  useEffect(() => {
    const uv = items.find((i) => i.code === "UV");
    const first = items.find((i) => i.code !== "UV");
    const pick = report.outcome_class === "POSITIVE" ? first : uv || first;
    setLines(report.services?.length ? report.services.map((s) => ({ code: s.code, qty: s.qty })) : pick ? [{ code: pick.code, qty: 1 }] : []);
    setNote("");
  }, [report.id, report.outcome_class, items]);

  const decide = async (decision) => {
    try {
      await api.post(`/production/reports/${report.id}/decision`, { decision, note, lines: lines.map((l) => ({ code: l.code, qty: Number(l.qty) })) });
      toast.success(decision === "approve" ? "Produzione validata" : "Rapportino respinto: ordine in rilavorazione");
      window.dispatchEvent(new Event("stats:refresh"));
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-6 border bg-white p-6 fade-up" data-testid="validation-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <TypeBadge type={order.service_type} />
          <div className="mt-1 font-mono text-2xl font-semibold">{order.external_order_id}</div>
          <div className="text-sm text-muted-foreground">{nameOf("parties", order.customer_id)} · {nameOf("projects", order.project_id, "code")} · {addressLine(order.address)}</div>
          <div className="mt-1 text-xs text-muted-foreground">Titolare {nameOf("companies", order.contract_company_id, "code")} · Esecutrice {nameOf("companies", order.executing_company_id, "code")}</div>
        </div>
        <div className="text-right text-sm">
          <div className="micro-label">Esito dichiarato</div>
          <div className={`font-display text-xl font-bold ${report.outcome_class === "POSITIVE" ? "text-emerald-700" : "text-urgent"}`}>{OUTCOME_LABEL[report.outcome]}</div>
        </div>
      </div>
      <div><div className="micro-label mb-2">Evidenze richieste dal committente</div><Evidence report={report} /></div>
      <ReportView report={report} />
      <div><div className="micro-label mb-2">Allegati</div><AttachmentGrid attachments={order.attachments} testId="validation-attachments" /></div>
      <div className="border-t pt-6">
        <div className="micro-label mb-3">Quantità valorizzabili · {report.price_list ? report.price_list.name : "nessun listino valido per area e periodo"}</div>
        <Lines items={items} lines={lines} setLines={setLines} disabled={!allowed} />
        <Textarea className="mt-4" placeholder="Nota di validazione (obbligatoria in caso di rifiuto)" value={note} onChange={(e) => setNote(e.target.value)} disabled={!allowed} data-testid="validation-note" />
        {!allowed && <div className="mt-3 text-sm text-muted-foreground">Solo Responsabile operativo o Amministratore possono validare.</div>}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" disabled={!allowed} onClick={() => decide("reject")} data-testid="validation-reject-button"><X className="mr-1 h-4 w-4" />Respingi</Button>
          <Button disabled={!allowed || !lines.length} onClick={() => decide("approve")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="validation-approve-button"><Check className="mr-1 h-4 w-4" />Approva e valorizza</Button>
        </div>
      </div>
    </div>
  );
};

export const ValidationQueue = () => {
  const { companyId } = useData();
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = useCallback(() => {
    api.get("/production/queue", { params: { company_id: companyId || undefined } }).then((r) => {
      setReports(r.data);
      setSelected((s) => r.data.find((x) => x.id === s?.id) || null);
    }).catch(() => {});
  }, [companyId]);
  useEffect(() => { load(); }, [load]);
  if (!reports.length) return <Empty text="Nessun intervento da validare" testId="validation-empty" />;
  return (
    <div className="border bg-white" data-testid="validation-list">
      {reports.map((r) => (
        <div key={r.id} className="border-b last:border-b-0">
          <button onClick={(e) => { const el = e.currentTarget; setSelected(selected?.id === r.id ? null : r); setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }}
            data-testid={`validation-item-${r.order.external_order_id}`} aria-expanded={selected?.id === r.id}
            className={`flex w-full scroll-mt-20 items-center gap-4 px-5 py-4 text-left transition-colors ${selected?.id === r.id ? "bg-accent" : "hover:bg-muted/60"}`}>
            <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${selected?.id === r.id ? "rotate-180" : ""}`} />
            <div className="flex-1">
              <div className="flex items-center justify-between"><span className="font-mono text-sm font-semibold">{r.order.external_order_id}</span><TypeBadge type={r.order.service_type} /></div>
              <div className="mt-1 text-xs text-muted-foreground">{r.technician_name} · {fmtDateTime(r.departed_at)} · tentativo #{r.attempt_no} · Esito: <span className="font-semibold text-foreground">{OUTCOME_LABEL[r.outcome]}</span></div>
            </div>
          </button>
          {selected?.id === r.id && <div className="border-t bg-muted/20 p-3 sm:p-4" data-testid={`validation-detail-${r.order.external_order_id}`}><Detail report={selected} onDone={load} /></div>}
        </div>
      ))}
    </div>
  );
};
